import { useEffect, useState } from "react";
import { useContracts } from "./useHook.";
import {
  EyeIcon,
  TrashIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";

const DESCRIPTION_MAX_WORDS = 100;

const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};


function Contracts() {
  const navigate = useNavigate();
  const { 
    createContract, 
    getContracts, 
    deleteContract, 
    updateContract,
    getAllContractCategories 
  } = useContracts();

  const [contracts, setContracts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const [offset, setOffset] = useState(0);
  const [limit] = useState(9);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");

  const fetchContracts = async (currentOffset = offset) => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.categoryId = categoryFilter;

      const res = await getContracts(currentOffset, limit, params);
      setContracts(res.data || []);
      setTotal(res.pagination?.totalRecords || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getAllContractCategories();
      setCategories(res || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchCategories();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleSearch = () => {
    setOffset(0);
    fetchContracts(0);
  };

  const handleReset = () => {
    setSearch("");
    setCategoryFilter("");
    setOffset(0);
    fetchContracts(0);
  };

  const handleUpload = async () => {
    if (!file || !title || !selectedCategoryId) return;
    if (countWords(description) > DESCRIPTION_MAX_WORDS) return;

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("file", file);
    formData.append("contract_category_id", selectedCategoryId);

    try {
      setUploading(true);
      await createContract(formData);

      setFile(null);
      setTitle("");
      setDescription("");
      setSelectedCategoryId("");
      setIsModalOpen(false);
      fetchContracts(0);

      Swal.fire("Success", "Contract uploaded successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const openEditModal = (contract) => {
    setEditId(contract?.id || null);
    setEditTitle(contract?.title || "");
    setEditDescription(contract?.description || "");
    setEditCategoryId(contract?.contract_category_id || "");
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editId || !editTitle || !editCategoryId) return;
    if (countWords(editDescription) > DESCRIPTION_MAX_WORDS) return;

    try {
      setUpdating(true);
      await updateContract(editId, {
        title: editTitle,
        description: editDescription,
        contract_category_id: editCategoryId
      });
      setIsEditModalOpen(false);
      setEditId(null);
      setEditTitle("");
      setEditDescription("");
      setEditCategoryId("");
      fetchContracts();
      Swal.fire("Success", "Contract updated successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Update failed", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this contract!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteContract(id);
        fetchContracts();

        Swal.fire("Deleted!", "Contract has been deleted.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };

  const handleView = (id) => {
    navigate(`/contracts/view/${id}`);
  };

  return (
    <div className="p-6 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">
          Contract Management
        </h2>

        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-600 transition duration-200 flex items-center gap-2"
        >
          <span className="text-xl">+</span> Add Contract
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap gap-4 items-center mb-8">
        <div className="flex-1 min-w-[250px] relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contracts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
          />
        </div>

        <div className="min-w-[200px]">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSearch}
            className="bg-gray-800 text-white px-5 py-2 rounded-lg hover:bg-gray-900 transition flex items-center gap-2"
          >
            <FunnelIcon className="h-4 w-4" /> Filter
          </button>
          <button
            onClick={handleReset}
            className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-300 transition"
          >
            Reset
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : contracts.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-dashed border-gray-300 text-center text-gray-500">
          No contracts found matching your criteria.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contracts.map((contract) => (
            <div
              key={contract.id}
              className="bg-white p-5 rounded-2xl shadow-md hover:shadow-lg transition duration-200 border border-gray-100 flex flex-col"
            >
              <div className="flex-1">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    {contract.category?.title || "Uncategorized"}
                  </span>
                </div>
                <h3 className="font-semibold text-lg text-gray-800 mb-2 truncate">
                  {contract.title}
                </h3>
                {!!contract.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 italic">
                    {contract.description}
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-2 flex-wrap mt-4 pt-4 border-t border-gray-50">
                <button
                  onClick={() => handleView(contract.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition duration-200 text-xs font-medium"
                >
                  <EyeIcon className="h-4 w-4" />
                  View
                </button>

                <button
                  onClick={() => openEditModal(contract)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition duration-200 text-xs font-medium"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  Edit
                </button>

                <button
                  onClick={() =>
                    navigate(`/contracts/user-contracts/${contract.id}`)
                  }
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition duration-200 text-xs font-medium"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  PDFs
                </button>

                <button
                  onClick={() => handleDelete(contract.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition duration-200 text-xs font-medium"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > limit && (
        <div className="mt-10">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-[500px] p-8 rounded-3xl shadow-2xl relative overflow-y-auto max-h-[95vh]">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>

            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              New Contract
            </h3>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Contract Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Tenancy Agreement"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Select Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                >
                  <option value="">Choose Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Summary of the contract..."
                  value={description}
                  onChange={(e) => {
                    const val = e.target.value;
                    const wordMatches = [...val.matchAll(/\S+/g)];
                    if (wordMatches.length <= DESCRIPTION_MAX_WORDS) {
                      setDescription(val);
                    }
                  }}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition min-h-[100px]"
                />
                <div className="flex justify-between mt-1 px-1">
                   <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Word count limit</p>
                   <p className={`text-[10px] font-bold ${countWords(description) >= DESCRIPTION_MAX_WORDS ? "text-red-500" : "text-gray-400"}`}>
                     {countWords(description)}/{DESCRIPTION_MAX_WORDS}
                   </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload Word Template (.docx) <span className="text-red-500">*</span>
                </label>
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group"
                >
                  <DocumentTextIcon className="w-8 h-8 text-gray-300 group-hover:text-blue-400 mb-2" />
                  <p className="text-gray-500 text-xs font-medium">
                    {file ? file.name : "Select or drop file"}
                  </p>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".doc,.docx"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !file || !title || !selectedCategoryId}
                  className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition disabled:opacity-50 flex justify-center items-center shadow-lg shadow-blue-200"
                >
                  {uploading ? "Uploading..." : "Save Contract"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-[450px] p-8 rounded-3xl shadow-2xl relative overflow-y-auto max-h-[90vh]">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Edit Contract
            </h3>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Contract Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={editCategoryId}
                  onChange={(e) => setEditCategoryId(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                >
                  <option value="">Choose Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => {
                    const val = e.target.value;
                    const wordMatches = [...val.matchAll(/\S+/g)];
                    if (wordMatches.length <= DESCRIPTION_MAX_WORDS) {
                      setEditDescription(val);
                    }
                  }}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition min-h-[100px]"
                />
                <div className="flex justify-end mt-1">
                   <p className={`text-[10px] font-bold ${countWords(editDescription) >= DESCRIPTION_MAX_WORDS ? "text-red-500" : "text-gray-400"}`}>
                     {countWords(editDescription)}/{DESCRIPTION_MAX_WORDS} words
                   </p>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={updating}
                  className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={updating || !editTitle || !editCategoryId}
                  className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition disabled:opacity-50 flex justify-center items-center shadow-lg shadow-blue-200"
                >
                  {updating ? "Saving..." : "Update Details"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contracts;